import React from "react";
import { usePortalMenu } from "../../../hooks/usePortalMenu";
import { TIME_OF_DAY_OPTIONS, TIMEZONE_NAMED_OPTIONS } from "../../../utils/scheduleConstants";
import type { EndsMode } from "../../../utils/storyRefreshTypes";
import { DateField } from "../DateField";
import { InlinePickDropdown } from "../InlinePickDropdown";
import { DistributionMonthlyRuleFields } from "../DistributionMonthlyRuleFields";
import { DistributionQuarterlyFields } from "../DistributionQuarterlyFields";
import { isRepeatUnit } from "../DistributionScheduleFields/DistributionScheduleFields.options";
import { NumberStepper } from "../NumberStepper";
import { WeekdayPillRow } from "../WeekdayPillRow";
import { ENDS_OPTIONS, SENTENCE_COPY as T, unitOptions } from "./ScheduleSentence.copy";
import { Comma, Sentence, Stack } from "./ScheduleSentence.styles";
import type { ScheduleSentenceProps } from "./ScheduleSentence.types";

const isEndsMode = (v: string): v is EndsMode => ENDS_OPTIONS.some((o) => o.value === v);

/**
 * "Every [n] [unit] on [rule] at [time] [zone], starting [date], ending [never | on date | after N sends]",
 * composed from the schedule molecules in their inline appearance. "once" reads "Send once on [date] at [time] [zone]".
 */
export const ScheduleSentence: React.FC<ScheduleSentenceProps> = (props) => {
  const { mode, value, todayIso, hour, weekdayPills, frequencyMenu, quarterMonthMenu, monthlyMenus, ruleModeMenu, endsMenu } = props;
  const { timeMenu, timezoneQuery, onChange, onToggleWeekday, onChangeMonthly, onChangeHour, onOpenTimeMenu, onCloseTimeMenu, onTimezoneQueryChange, className } = props;
  const hourPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({ open: timeMenu === "hour", onOutsideClick: onCloseTimeMenu });
  const tzPortal = usePortalMenu<HTMLButtonElement, HTMLUListElement>({ open: timeMenu === "timezone", onOutsideClick: onCloseTimeMenu });

  /** The time and timezone pickers; `trailingComma` keeps a comma on the last one ("…Los Angeles, starting…"). */
  const timePicks = (trailingComma: boolean) => (
    <>
      {T.at}
        <InlinePickDropdown
          value={hour === null ? null : String(hour)}
          options={TIME_OF_DAY_OPTIONS}
          open={timeMenu === "hour"}
          triggerRef={hourPortal.triggerRef}
          menuRef={hourPortal.menuRef}
          menuRect={hourPortal.rect}
          placeholder="Pick a time"
          ariaLabel="Time"
          onToggle={() => onOpenTimeMenu("hour")}
          onSelect={(v) => {
            onChangeHour(Number(v));
            onCloseTimeMenu();
          }}
        />
      <Comma $show={trailingComma}>
          <InlinePickDropdown
            value={value.timezone}
            options={TIMEZONE_NAMED_OPTIONS}
            open={timeMenu === "timezone"}
            triggerRef={tzPortal.triggerRef}
            menuRef={tzPortal.menuRef}
            menuRect={tzPortal.rect}
            showScrollbar
            searchable
            query={timezoneQuery}
            onQueryChange={onTimezoneQueryChange}
            searchPlaceholder="Search timezone"
            placeholder="Timezone"
            ariaLabel="Timezone"
            onToggle={() => onOpenTimeMenu("timezone")}
            onSelect={(v) => {
              onChange({ timezone: v });
              onCloseTimeMenu();
            }}
          />
      </Comma>
    </>
  );

  if (mode === "once") {
    return (
      <Sentence className={className}>
        {T.once}
          <DateField appearance="sentence" value={value.onceDate} min={todayIso} ariaLabel="Send date" iconLayout="calendar-end" onChange={(onceDate) => onChange({ onceDate })} />
        {timePicks(false)}
      </Sentence>
    );
  }

  const monthly = value.unit === "month" || value.unit === "quarter";
  return (
    <Stack className={className}>
      <Sentence>
        {T.every}
          <NumberStepper appearance="sentence" value={value.every} min={1} max={99} ariaLabel="Repeat interval" onChange={(every) => onChange({ every })} />
          <InlinePickDropdown
            value={value.unit}
            options={unitOptions(value.every)}
            open={frequencyMenu.open}
            triggerRef={frequencyMenu.triggerRef}
            menuRef={frequencyMenu.menuRef}
            menuRect={frequencyMenu.menuRect}
            ariaLabel="Repeats"
            onToggle={frequencyMenu.onToggle}
            onSelect={(unit) => {
              if (isRepeatUnit(unit)) onChange({ unit });
              frequencyMenu.onClose();
            }}
          />
        {value.unit === "quarter" ? (
          <>
            {T.inTheQuarterMonth}
              <DistributionQuarterlyFields appearance="inline" month={value.quarterMonth} monthMenu={quarterMonthMenu} onChangeMonth={(quarterMonth) => onChange({ quarterMonth })} />
          </>
        ) : null}
        {monthly ? (
          <>
            {T.on}
            <DistributionMonthlyRuleFields appearance="inline" id="sentence-monthly" label="" value={value.monthly} menus={monthlyMenus} modeMenu={ruleModeMenu} onChange={onChangeMonthly} />
          </>
        ) : null}
        {timePicks(true)}
        {T.starting}
        <Comma $show>
            <DateField appearance="sentence" value={value.startsOn} min={todayIso} ariaLabel="Start date" iconLayout="calendar-end" onChange={(startsOn) => onChange({ startsOn })} />
        </Comma>
        {T.ending}
          <InlinePickDropdown
            value={value.endsMode}
            options={ENDS_OPTIONS}
            open={endsMenu.open}
            triggerRef={endsMenu.triggerRef}
            menuRef={endsMenu.menuRef}
            menuRect={endsMenu.menuRect}
            ariaLabel="Ends"
            onToggle={endsMenu.onToggle}
            onSelect={(m) => {
              if (isEndsMode(m)) onChange({ endsMode: m });
              endsMenu.onClose();
            }}
          />
        {value.endsMode === "on" ? (
            <DateField appearance="sentence" value={value.endsOn} min={value.startsOn} placeholder="Pick an end date" ariaLabel="Schedule end date" iconLayout="calendar-end" onChange={(endsOn) => onChange({ endsOn })} />
        ) : null}
        {value.endsMode === "after" ? (
          <>
              <NumberStepper appearance="sentence" value={value.endsAfter} min={1} max={999} ariaLabel="Number of sends" onChange={(endsAfter) => onChange({ endsAfter })} />
            {T.sends}
          </>
        ) : null}
      </Sentence>
      {value.unit === "week" ? <WeekdayPillRow value={weekdayPills} onToggle={onToggleWeekday} layout="start" /> : null}
    </Stack>
  );
};

export default ScheduleSentence;
